import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Group, Layout, Page, Tab, Tree } from "@/comn/components";
import { useFetch, useForm, useModal, useToast, useTab, useResource, useTree, usePage } from "@/comn/hooks";
import {
    APIS,
    SF_ORG_USR_INFO,
    URLS,
    CSTM_OFCE_CD_RQRD_ARR,
} from "@/tra/tancis/ptli/usr/orgusr/services/PtliOrgUsrService";

/*
 * Modifying and Deleting Organization User Management
 * !== 기관 관리 수정 및 삭제 ==!
 */
export const PTLI0917003S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const tree = useTree(); // Tree Hook !== Tree Hook ==!
    const { xtrnUsrUuid } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    useResource({
        defaultSchema: [{ area: "orgDeptCd" }, { area: "orgCd" }, { area: "cntyCd" }, { area: "cstmCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization User Information Form !== 기관 사용자 정보 form ==!
        orgUsrInfo: useForm({
            defaultSchema: SF_ORG_USR_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Organization User Information !== 기관 사용자 정보 조회 ==!
        getOrgUsrInfo: useFetch({
            api: (data) => APIS.getOrgUsrInfo(xtrnUsrUuid),
            enabled: !!xtrnUsrUuid,
            onSuccess: (data) => {
                const orgUsrInfo = data.orgUsrInfo.content;
                form.orgUsrInfo.setValues(orgUsrInfo);
                form.orgUsrInfo.setValue("usrPwd", "");
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00018" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        // Update Organization User Information !== 기관 사용자 정보 수정 ==!
        updateOrgInfo: useFetch({
            api: (info: any) => APIS.updateOrgUsrInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => navigate(`${URLS.ptli0917001q}?menuId=${menuId}`),
                    close: false,
                });
            },
            onError: (err) => {
                toast.showToast({ type: "warning", content: err.response.data.errorMessage }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => navigate(`${URLS.ptli0917001q}?menuId=${menuId}`),
        // Click Update Button !== 수정 버튼 클릭 ==!
        onUpdateBtnClick: form.orgUsrInfo.handleSubmit(
            () => {
                if (CSTM_OFCE_CD_RQRD_ARR.includes(form.orgUsrInfo.getValue("orgCd"))) {
                    if (comnUtils.isEmpty(form.orgUsrInfo.getValue("cstmOfceCd"))) {
                        form.orgUsrInfo.setError("cstmOfceCd", {
                            message: "msg.com.00005", // Mandatory fields !== 필수 입력 필드 ==!
                            type: "error",
                        });
                        return;
                    }
                }

                modal.openModal({
                    content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
                    onConfirm: () => fetch.updateOrgInfo.fetch(form.orgUsrInfo.getValues()),
                });
            },
            () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        ),
        onUsrStatChange: (value: any) => {
            if (value == 2) {
                form.orgUsrInfo.setSchema("mdfyRsn", { required: true });
            } else {
                form.orgUsrInfo.setSchema("mdfyRsn", { required: false });
            }
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgUsrInfo.fetch();
        form.orgUsrInfo.setSchema("psnTin", { edit: false });
        form.orgUsrInfo.setSchema("orgCd", { edit: false });
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.psnTin} />
                            <Group.Control {...form.orgUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgCd} />
                            <Group.Control {...form.orgUsrInfo.schema.deptCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.frgnrYn} />
                            <Group.Control {...form.orgUsrInfo.schema.aplcCntyCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrMoblNo} />
                            <Group.Control {...form.orgUsrInfo.schema.usrEmlAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.rsdtAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrStatCd} onChange={handler.onUsrStatChange} />
                            <Group.Control {...form.orgUsrInfo.schema.orgRppnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.mdfyRsn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.cstmOfceCd} />
                            <Group.Cell size={6}>{t("msg.ptl.00799")}</Group.Cell>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onUpdateBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
